import { AlertTriangle, Shield, Clock, Users, MessageSquare } from 'lucide-react';

interface AdminIncidentResponseProps {
  currentUserRole: 'root' | 'admin';
}

export const AdminIncidentResponse = ({ currentUserRole: _currentUserRole }: AdminIncidentResponseProps) => {
  // Suppress unused variable warning - reserved for future role-based content
  void _currentUserRole;
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 rounded-t-lg">
        <div className="flex items-center gap-3">
          <AlertTriangle size={28} className="text-red-100" />
          <div>
            <h1 className="text-2xl font-bold">Incident Response SOPs</h1>
            <p className="text-red-100 mt-1">
              Standard Operating Procedures for responding to incidents affecting platform integrity, legal safety, and user safety
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 lg:p-8 space-y-8">
        {/* Purpose Section */}
        <section className="mb-8">
          <div className="bg-red-50 border-l-4 border-red-600 p-4 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Purpose</h2>
            <p className="text-gray-700">
              This document defines how VerifyPH responds to incidents that may affect platform integrity, legal safety, public trust, or user safety.
            </p>
          </div>

          <div className="bg-gray-50 border-l-4 border-gray-400 p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Core Principle</h3>
            <p className="text-gray-700 mb-2">
              VerifyPH documents citizen-submitted evidence.
            </p>
            <p className="text-gray-700 font-semibold">
              Incident response prioritizes safety, neutrality, legality, and transparency over speed.
            </p>
          </div>
        </section>

        {/* Section 1: Incident Classification */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Incident Classification</h2>
          <p className="text-gray-700 mb-4">All incidents must be classified immediately upon detection.</p>

          <div className="space-y-4">
            <div className="bg-green-50 border-l-4 border-green-600 p-5 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="text-green-600" size={20} />
                Level 1 — Low Risk
              </h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Minor UI bugs</li>
                <li>Duplicate reports</li>
                <li>Routine moderation disputes</li>
                <li>Non-viral complaints</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-5 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="text-yellow-600" size={20} />
                Level 2 — Medium Risk
              </h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Coordinated abuse or brigading</li>
                <li>Election-period sensitivity</li>
                <li>Complaints from public officials or LGUs</li>
                <li>Misinterpretation by users or media</li>
              </ul>
            </div>

            <div className="bg-red-50 border-l-4 border-red-600 p-5 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="text-red-600" size={20} />
                Level 3 — High Risk
              </h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Legal threats (lawyers, cease-and-desist)</li>
                <li>COMELEC complaints</li>
                <li>Media investigations</li>
                <li>Threats to personal safety</li>
                <li>Data breach or unauthorized access</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 2: Incident Roles & Authority */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Incident Roles & Authority</h2>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Users className="text-blue-600" size={18} />
                Admin
              </h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Identify and log incidents</li>
                <li>Temporarily hide content if needed</li>
                <li>Escalate to Root Admin</li>
              </ul>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Shield className="text-purple-600" size={18} />
                Root Admin
              </h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Lead response for Level 2–3 incidents</li>
                <li>Approve public statements</li>
                <li>Coordinate legal or advisory input</li>
                <li>Decide on takedown vs restoration</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <MessageSquare className="text-green-600" size={18} />
                Communications Lead
              </h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Draft neutral public responses</li>
                <li>Handle media inquiries</li>
                <li>Ensure message discipline</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 3: Universal First Response */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Universal First Response (ALL INCIDENTS)</h2>
          <p className="text-gray-700 mb-4">Upon detecting an incident:</p>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-5 rounded-lg space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">1. Stay Calm</h3>
              <p className="text-sm text-gray-700">Do not respond emotionally or defensively</p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">2. Preserve Evidence</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Do NOT delete content</li>
                <li>Take screenshots if needed</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">3. Assess Risk Level</h3>
              <p className="text-sm text-gray-700">Classify as Level 1, 2, or 3</p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">4. Log the Incident</h3>
              <p className="text-sm text-gray-700 mb-1">Record in <code className="bg-blue-100 px-1 rounded">auditLogs</code>:</p>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside ml-4">
                <li>Incident type</li>
                <li>Timestamp</li>
                <li>Reporter (if applicable)</li>
                <li>Initial assessment</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">5. Apply Temporary Controls</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Hide content if there is potential risk</li>
                <li>Do not publish new related content</li>
              </ul>
            </div>
          </div>

          <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded">
            <p className="text-sm font-semibold text-yellow-900">
              Golden Rule: Visibility can wait. Legal safety cannot.
            </p>
          </div>
        </section>

        {/* Section 4: Level 1 Incident SOP */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Level 1 Incident SOP (Low Risk)</h2>

          <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Examples</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>User disputes moderation decision</li>
              <li>Duplicate or low-quality submissions</li>
              <li>UI-related complaints</li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Actions</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Review content using moderation checklist</li>
              <li>Respond politely with factual explanation</li>
              <li>Restore or reject content as appropriate</li>
              <li>Log outcome</li>
            </ul>
          </div>

          <div className="bg-gray-50 border-l-4 border-gray-500 p-4 rounded">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Clock className="text-gray-600" size={18} />
              Response Timeline
            </h3>
            <p className="text-sm text-gray-700">Within 48 hours</p>
          </div>
        </section>

        {/* Section 5: Level 2 Incident SOP */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Level 2 Incident SOP (Medium Risk)</h2>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Examples</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Election-period complaints</li>
              <li>Coordinated reporting or brigading</li>
              <li>Complaints from local officials</li>
              <li>Misleading social media narratives</li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-5 rounded-lg mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Actions</h3>
            <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
              <li>Hide affected content (temporary)</li>
              <li>Escalate to Root Admin</li>
              <li>Review:
                <ul className="list-disc list-inside ml-6 mt-1">
                  <li>Evidence</li>
                  <li>Language neutrality</li>
                  <li>Moderation history</li>
                </ul>
              </li>
              <li>Decide:
                <ul className="list-disc list-inside ml-6 mt-1">
                  <li>Restore with clarification</li>
                  <li>Keep hidden pending review</li>
                </ul>
              </li>
              <li>Prepare internal summary</li>
            </ol>
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-600 p-4 rounded mb-4">
            <h3 className="font-semibold text-gray-900 mb-2">Communications</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Use neutral, non-argumentative language</li>
              <li>Do NOT engage in public debate</li>
            </ul>
          </div>

          <div className="bg-gray-50 border-l-4 border-gray-500 p-4 rounded">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Clock className="text-gray-600" size={18} />
              Response Timeline
            </h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Initial action within 24 hours</li>
              <li>Resolution within 72 hours</li>
            </ul>
          </div>
        </section>

        {/* Section 6: Level 3 Incident SOP */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Level 3 Incident SOP (High Risk)</h2>

          <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Examples</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>COMELEC complaints</li>
              <li>Legal notices or threats</li>
              <li>Media investigations</li>
              <li>Security or data incidents</li>
            </ul>
          </div>

          <div className="bg-red-100 border-l-4 border-red-600 p-5 rounded-lg mb-4">
            <h3 className="font-semibold text-red-900 mb-3">Immediate Actions (Within 12 Hours)</h3>
            <ol className="text-sm text-red-800 space-y-2 list-decimal list-inside font-medium">
              <li>Freeze related moderation actions</li>
              <li>Hide affected content</li>
              <li>Escalate to Root Admin immediately</li>
              <li>Preserve all logs and evidence</li>
              <li>Restrict internal access to need-to-know</li>
            </ol>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-5 rounded-lg mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Review Process</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Root Admin conducts full review</li>
              <li>Consult legal counsel or advisors if available</li>
              <li>Validate compliance with:
                <ul className="list-disc list-inside ml-6 mt-1">
                  <li>Methodology</li>
                  <li>Terms of Service</li>
                  <li>Published disclaimers</li>
                </ul>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 border-l-4 border-gray-600 p-4 rounded">
            <h3 className="font-semibold text-gray-900 mb-2">Decision Options</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Maintain hidden status</li>
              <li>Restore with added disclaimer</li>
              <li>Permanent removal (only if justified)</li>
            </ul>
            <p className="text-sm text-gray-700 mt-2 font-semibold">
              All decisions must be documented.
            </p>
          </div>
        </section>

        {/* Section 7: COMELEC / Election-Related Incident SOP */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">7. COMELEC / Election-Related Incident SOP</h2>

          <div className="bg-red-50 border-l-4 border-red-600 p-5 rounded-lg mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Trigger</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Any reference to COMELEC</li>
              <li>Election-related complaint</li>
              <li>Candidate or party allegation</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-600 p-5 rounded-lg mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Mandatory Actions</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Classify as Level 3</li>
              <li>Hide affected content immediately</li>
              <li>Escalate to Root Admin</li>
              <li>Prepare neutral response using approved template</li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-5 rounded-lg mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Approved Response Position</h3>
            <blockquote className="text-sm text-gray-700 italic border-l-4 border-blue-500 pl-4">
              VerifyPH is a non-partisan transparency platform documenting citizen-submitted observations supported by evidence. 
              The platform does not endorse or oppose any candidate or party and does not make legal determinations.
            </blockquote>
          </div>

          <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
            <h3 className="font-semibold text-red-900 mb-2">Do NOT:</h3>
            <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
              <li>Argue election law interpretations</li>
              <li>Engage politically</li>
              <li>Remove unrelated content</li>
            </ul>
          </div>
        </section>

        {/* Section 8: Media Inquiry SOP */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Media Inquiry SOP</h2>

          <div className="bg-yellow-50 border-l-4 border-yellow-600 p-5 rounded-lg mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">When Contacted by Media</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Do NOT comment immediately</li>
              <li>Acknowledge receipt politely</li>
              <li>Escalate to Root Admin / Communications Lead</li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-5 rounded-lg mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Approved Talking Points</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Evidence-based documentation</li>
              <li>Neutral moderation process</li>
              <li>Published methodology and disclaimers</li>
              <li>No political endorsement</li>
            </ul>
          </div>

          <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
            <p className="text-sm text-red-800 font-semibold">
              Never speculate or assign blame.
            </p>
          </div>
        </section>

        {/* Section 9: Security or Data Incident SOP */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Security or Data Incident SOP</h2>

          <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Examples</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Unauthorized access</li>
              <li>Data leakage</li>
              <li>Account compromise</li>
            </ul>
          </div>

          <div className="bg-red-100 border-l-4 border-red-600 p-5 rounded-lg mb-4">
            <h3 className="font-semibold text-red-900 mb-3">Immediate Actions</h3>
            <ol className="text-sm text-red-800 space-y-2 list-decimal list-inside font-medium">
              <li>Restrict access</li>
              <li>Identify affected systems</li>
              <li>Preserve logs</li>
              <li>Notify Root Admin</li>
            </ol>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-5 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Follow-Up</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Assess scope</li>
              <li>Patch vulnerability</li>
              <li>Notify affected users if required</li>
              <li>Document incident and resolution</li>
            </ul>
          </div>
        </section>

        {/* Section 10: Restoration & Post-Incident Review */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Restoration & Post-Incident Review</h2>
          <p className="text-gray-700 mb-4">After resolution:</p>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-5 rounded-lg">
            <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
              <li>Review incident handling</li>
              <li>Identify gaps or delays</li>
              <li>Update SOPs if needed</li>
              <li>Conduct admin debrief</li>
            </ul>

            <div className="mt-4 bg-white border-l-4 border-blue-500 p-4 rounded">
              <p className="text-sm font-semibold text-gray-900 mb-1">Key Question:</p>
              <p className="text-sm text-gray-700 italic">
                How do we prevent this from escalating next time?
              </p>
            </div>
          </div>
        </section>

        {/* Section 11: Documentation Requirements */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Documentation Requirements (Non-Negotiable)</h2>
          <p className="text-gray-700 mb-4">Every incident must have:</p>

          <div className="bg-yellow-50 border-l-4 border-yellow-600 p-5 rounded-lg mb-4">
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside mb-4">
              <li>Classification level</li>
              <li>Timeline of actions</li>
              <li>Decision rationale</li>
              <li>Final outcome</li>
            </ul>

            <div className="bg-white border border-yellow-300 rounded p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Stored in:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li><code className="bg-yellow-100 px-1 rounded">auditLogs</code></li>
                <li>Internal incident tracker (if used)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 12: Final Rule */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Final Rule</h2>
          
          <div className="bg-red-50 border-l-4 border-red-600 p-5 rounded">
            <p className="text-gray-900 mb-4">
              If an incident feels uncomfortable, politically sensitive, or legally unclear:
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-gray-800 font-semibold">→ <strong>Hide</strong></p>
              <p className="text-gray-800 font-semibold">→ <strong>Escalate</strong></p>
              <p className="text-gray-800 font-semibold">→ <strong>Document</strong></p>
            </div>
            <div className="bg-white border border-red-300 rounded p-4">
              <p className="text-gray-800 font-semibold">
                VerifyPH prioritizes long-term trust over short-term visibility.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
